/**
 * /admin/campaign-sends — سجلّ موحَّد لإرسالات حملات التذكير
 * (wishlist / abandoned_cart / occasion / tier_progress).
 */
import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, CheckCircle2, XCircle, MessageSquare, Mail, Sparkles, UserSearch } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useLocale } from "@/contexts/LocaleContext";
import { SiteShell } from "@/components/SiteShell";
import { AdminNav } from "@/components/admin/AdminNav";
import { Button } from "@/components/ui/button";
import { listCampaignSends } from "@/utils/campaignSendsLog.functions";

type CampaignFilter = "all" | "wishlist" | "abandoned_cart" | "occasion" | "tier_progress" | "tier_progress_ambassador";
type StatusFilter = "all" | "sent" | "failed";
type ChannelFilter = "all" | "whatsapp" | "email";

const CAMPAIGN_KEYS: CampaignFilter[] = ["all", "wishlist", "abandoned_cart", "occasion", "tier_progress", "tier_progress_ambassador"];

export const Route = createFileRoute("/admin/campaign-sends")({
  component: AdminCampaignSendsPage,
  validateSearch: (search: Record<string, unknown>): { campaign: CampaignFilter } => {
    const c = search.campaign;
    return { campaign: typeof c === "string" && (CAMPAIGN_KEYS as string[]).includes(c) ? (c as CampaignFilter) : "all" };
  },
  head: () => ({
    meta: [
      { title: "Campaign Sends · Lecmo Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
});

function AdminCampaignSendsPage() {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useIsAdmin();
  const { dir } = useLocale();
  const isAr = dir === "rtl";
  const fn = useServerFn(listCampaignSends);
  const { campaign: initialCampaign } = Route.useSearch();

  const [campaignKey, setCampaignKey] = React.useState<CampaignFilter>(initialCampaign);
  const [status, setStatus] = React.useState<StatusFilter>("all");
  const [channel, setChannel] = React.useState<ChannelFilter>("all");
  const [page, setPage] = React.useState(1);
  const pageSize = 30;

  const { data, isLoading } = useQuery({
    queryKey: ["admin-campaign-sends", campaignKey, status, channel, page],
    queryFn: () => fn({ data: { campaignKey, status, channel, page, pageSize } }),
    enabled: !!user && isAdmin,
  });

  if (authLoading || roleLoading) {
    return (
      <SiteShell>
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </SiteShell>
    );
  }

  if (!user || !isAdmin) {
    return (
      <SiteShell>
        <div className="container max-w-2xl py-16 text-center">
          <p className="text-muted-foreground">{isAr ? "هذه الصفحة للمسؤولين فقط." : "Admins only."}</p>
          <Link to="/" className="mt-4 inline-block text-sm underline">{isAr ? "العودة" : "Back"}</Link>
        </div>
      </SiteShell>
    );
  }

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const campaignFilters: { key: CampaignFilter; label: string }[] = [
    { key: "all", label: isAr ? "كلّ الحملات" : "All" },
    { key: "wishlist", label: isAr ? "قائمة الرغبات" : "Wishlist" },
    { key: "abandoned_cart", label: isAr ? "السلّة المتروكة" : "Cart" },
    { key: "occasion", label: isAr ? "المناسبات" : "Occasion" },
    { key: "tier_progress", label: isAr ? "ترقية العملاء" : "Customer tier" },
    { key: "tier_progress_ambassador", label: isAr ? "ترقية السفراء" : "Ambassador tier" },
  ];
  const statusFilters: { key: StatusFilter; label: string }[] = [
    { key: "all", label: isAr ? "كلّ الحالات" : "All" },
    { key: "sent", label: isAr ? "نجح" : "Sent" },
    { key: "failed", label: isAr ? "فشل" : "Failed" },
  ];
  const channelFilters: { key: ChannelFilter; label: string }[] = [
    { key: "all", label: isAr ? "كلّ القنوات" : "All" },
    { key: "whatsapp", label: "WhatsApp" },
    { key: "email", label: "Email" },
  ];

  const sentCount = rows.filter((r: any) => r.status === "sent").length;
  const failedCount = rows.filter((r: any) => r.status === "failed").length;

  return (
    <SiteShell>
      <div className="mx-auto w-full max-w-6xl px-4 py-8 sm:px-6 space-y-6">
        <AdminNav />
        <header className="space-y-1">
          <h1 className="text-2xl font-semibold">{isAr ? "سجلّ إرسالات الحملات" : "Campaign Sends Log"}</h1>
          <p className="text-sm text-muted-foreground">
            {isAr
              ? "كلّ رسائل التذكير المُرسَلة. عمود «النوع» يميّز التذكير بلا خصم عن التذكير المرفق بكود."
              : "All reminder messages sent. The «Type» column distinguishes plain reminders from those carrying a discount code."}
          </p>
        </header>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3">
          <Stat label={isAr ? "المجموع" : "Total"} value={total} icon={<Sparkles className="h-4 w-4" />} />
          <Stat label={isAr ? "نجح (هذه الصفحة)" : "Sent (page)"} value={sentCount} icon={<CheckCircle2 className="h-4 w-4 text-green-600" />} />
          <Stat label={isAr ? "فشل (هذه الصفحة)" : "Failed (page)"} value={failedCount} icon={<XCircle className="h-4 w-4 text-destructive" />} />
        </div>

        {/* Filters */}
        <div className="space-y-2">
          <FilterRow label={isAr ? "الحملة" : "Campaign"} options={campaignFilters} value={campaignKey} onChange={(v) => { setCampaignKey(v); setPage(1); }} />
          <FilterRow label={isAr ? "الحالة" : "Status"} options={statusFilters} value={status} onChange={(v) => { setStatus(v); setPage(1); }} />
          <FilterRow label={isAr ? "القناة" : "Channel"} options={channelFilters} value={channel} onChange={(v) => { setChannel(v); setPage(1); }} />
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">
            {isAr ? "لا توجد إرسالات بعد." : "No sends yet."}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-none border border-border">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-muted/50">
                  <tr>
                    <th className="px-3 py-2 text-start font-medium">{isAr ? "التاريخ" : "Date"}</th>
                    <th className="px-3 py-2 text-start font-medium">{isAr ? "الحملة" : "Campaign"}</th>
                    <th className="px-3 py-2 text-start font-medium">{isAr ? "الخطوة" : "Step"}</th>
                    <th className="px-3 py-2 text-start font-medium">{isAr ? "العميل" : "Customer"}</th>
                    <th className="px-3 py-2 text-start font-medium">{isAr ? "القناة" : "Channel"}</th>
                    <th className="px-3 py-2 text-start font-medium">{isAr ? "النوع" : "Type"}</th>
                    <th className="px-3 py-2 text-start font-medium">{isAr ? "الحالة" : "Status"}</th>
                    <th className="px-3 py-2 text-start font-medium">{isAr ? "الخطأ" : "Error"}</th>
                    <th className="px-3 py-2 text-start font-medium">{isAr ? "إجراء" : "Action"}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r: any) => (
                    <tr key={r.id} className="hover:bg-muted/30">
                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">{fmtDate(r.sent_at)}</td>
                      <td className="px-3 py-2">
                        <div className="text-sm">{isAr ? r.campaign_name_ar : r.campaign_name_en}</div>
                        <div className="text-[10px] text-muted-foreground">{r.campaign_key ?? "—"}</div>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {r.step_order != null ? (
                          <span className="text-muted-foreground">
                            #{r.step_order} · {r.delay_days}d
                          </span>
                        ) : "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        <div>{r.customer_name ?? "—"}</div>
                        <div className="text-muted-foreground" dir="ltr">{r.customer_email ?? r.customer_phone ?? r.user_id?.slice(0, 8)}</div>
                      </td>
                      <td className="px-3 py-2">
                        <ChannelBadge channel={r.channel} />
                      </td>
                      <td className="px-3 py-2"><TypeBadge code={r.coupon_code ?? null} isAr={isAr} /></td>
                      <td className="px-3 py-2">
                        <StatusBadge status={r.status} isAr={isAr} />
                      </td>
                      <td className="px-3 py-2 text-xs text-destructive max-w-[200px] truncate" title={r.error ?? ""}>
                        {r.error ?? "—"}
                      </td>
                      <td className="px-3 py-2">
                        {r.user_id ? (
                          <Link
                            to="/admin/crm"
                            search={{ user: r.user_id, q: undefined }}
                            className="inline-flex min-h-[36px] items-center gap-1 rounded-none border border-border px-2 py-1 text-[11px] hover:bg-muted"
                            title={isAr ? "افتح بطاقة العميل في CRM" : "Open in CRM"}
                          >
                            <UserSearch className="h-3 w-3" />
                            {isAr ? "CRM" : "CRM"}
                          </Link>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{isAr ? `${total} نتيجة` : `${total} results`}</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                    {isAr ? "السابق" : "Prev"}
                  </Button>
                  <span className="flex items-center px-2 tabular-nums">{page}/{totalPages}</span>
                  <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                    {isAr ? "التالي" : "Next"}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </SiteShell>
  );
}

function FilterRow<T extends string>({
  label, options, value, onChange,
}: {
  label: string;
  options: { key: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground min-w-[60px]">{label}:</span>
      {options.map((o) => (
        <Button
          key={o.key}
          size="sm"
          variant={value === o.key ? "default" : "outline"}
          className="text-xs h-7"
          onClick={() => onChange(o.key)}
        >
          {o.label}
        </Button>
      ))}
    </div>
  );
}

function Stat({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="rounded-none border border-border bg-muted/20 p-3">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">{icon}{label}</div>
      <div className="mt-1 text-xl font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ChannelBadge({ channel }: { channel: string | null }) {
  if (channel === "whatsapp") {
    return <span className="inline-flex items-center gap-1 text-xs"><MessageSquare className="h-3 w-3" />WA</span>;
  }
  if (channel === "email") {
    return <span className="inline-flex items-center gap-1 text-xs"><Mail className="h-3 w-3" />Email</span>;
  }
  return <span className="text-xs text-muted-foreground">—</span>;
}

function TypeBadge({ code, isAr }: { code: string | null; isAr: boolean }) {
  if (code) {
    return (
      <div className="flex flex-col gap-0.5">
        <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">
          {isAr ? "تذكير + كود" : "Reminder + code"}
        </span>
        <span className="font-mono text-[11px] text-muted-foreground" dir="ltr">{code}</span>
      </div>
    );
  }
  return (
    <span className="inline-flex w-fit items-center rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
      {isAr ? "تذكير فقط" : "Reminder only"}
    </span>
  );
}

function StatusBadge({ status, isAr }: { status: string; isAr: boolean }) {
  if (status === "sent") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-700">
        <CheckCircle2 className="h-3 w-3" />{isAr ? "نجح" : "Sent"}
      </span>
    );
  }
  if (status === "failed") {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
        <XCircle className="h-3 w-3" />{isAr ? "فشل" : "Failed"}
      </span>
    );
  }
  return <span className="text-xs text-muted-foreground">{status}</span>;
}

function fmtDate(d: string | null): string {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString(undefined, {
      year: "2-digit", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return d;
  }
}
